import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { ContactPointType } from '../../../../api/app-types/contact-point.app';
import { InstitutionExtended } from '../../../../api/app-types/institution.app';
import { NeighborContext } from '../../../../api/app-types/neighbors.app';
import { RoleId } from '../../../../api/app-types/role.app';
import { User } from '../../../../api/app-types/user.app';
import { Workplace } from '../../../../api/app-types/workplace.app';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { CarouselElement, NavCarousel } from '../../../../core/components/nav-carousel/nav-carousel';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { RouteInjection } from '../../../../shared/routing/route-injection.class';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { InstitutionWorkplaceDeleteDialog } from '../institution-workplace-delete-dialog/institution-workplace-delete-dialog';
import { RECORD_MANAGER } from '../../../../core/services/record.provide';
import { InstitutionActivity } from '../../institution-activity/institution-activity';

@Component({
	selector: 'lvr-institution-workplace-details',
	imports: [NavCarousel, TranslocoPipe, LvrHeading, RouterLink, InstitutionActivity],
	templateUrl: './institution-workplace-details.html',
	styleUrl: './institution-workplace-details.scss',
})
export class InstitutionWorkplaceDetails extends RouteInjection {
	private readonly data = toSignal(this.route.data);
	private readonly session = inject(SessionManagerService);
	private readonly recordManager = inject(RECORD_MANAGER);
	private readonly workplaceNeighbors = this.resolveDataAsSignal<NeighborContext<Workplace>>('workplaceNeighbors');
	public readonly institution = computed(() => this.data()!['institution'] as InstitutionExtended);
	private readonly dialog = inject(MatDialog);
	private title = signal<string>('');

	public readonly eaaRole = RoleId.EAA;
	public readonly types = {
		phone: ContactPointType.PHONE,
		email: ContactPointType.EMAIL,
	};

	public readonly user = computed(() => this.data()!['user'] as User);
	public readonly workplace = computed(() => this.data()!['workplace'] as Workplace);
	private readonly isMainSchool = computed(
		() => this.institution().schoolNumber !== undefined && this.workplace().isMainInstitution,
	);
	private readonly isSchoolBranch = computed(() => this.workplace().schoolNumber !== undefined);
	public readonly isSchool = computed(() => this.isMainSchool() || this.isSchoolBranch());
	public readonly schoolNumber = computed(() => this.workplace().schoolNumber ?? this.institution().schoolNumber);
	private readonly hasInstitutionEditPermission = hasPermission(this.session.permissions, 'Institutions.Edit');
	private readonly hasInstitutionDeletePermission = hasPermission(this.session.permissions, 'Institutions.Delete');
	public readonly canEditInstitutions = computed(() => this.hasInstitutionEditPermission() && !this.isSchool());
	public readonly canDeleteInstitutions = computed(() => this.hasInstitutionDeletePermission() && !this.isSchool());

	public constructor() {
		super();
		effect(() => {
			const institution = this.institution();
			if (institution.name !== this.title()) {
				this.title.set(institution.name);
				this.recordManager.updateRecordTitleOfActiveRecord(this.title());
			}
		});
	}

	public readonly carouselElements = computed(() => {
		const neighbors = this.workplaceNeighbors();

		if (!neighbors) return [];

		return [neighbors.previous, neighbors.current, neighbors.next]
			.filter((wp): wp is Workplace => !!wp)
			.map((wp) => ({
				id: wp.id!.toString(),
				label: wp.name,
			}));
	});

	public readonly currentIndex = computed(() => {
		const workplace = this.workplace();
		const elements = this.carouselElements();

		if (!workplace || elements.length === 0) return 0;

		const index = elements.findIndex((e) => e.id === workplace.id!.toString());

		return index >= 0 ? index : 0;
	});

	public readonly backLink = computed(() => this.router.createUrlTree(['../'], { relativeTo: this.route }).toString());

	public readonly preferredCommunicationPoints = computed(
		() => this.workplace().contactPoints?.filter((cp) => cp.preferredWayOfContact) ?? [],
	);

	public readonly otherCommunicationPoints = computed(
		() => this.workplace().contactPoints?.filter((cp) => !cp.preferredWayOfContact) ?? [],
	);

	public readonly responsibleInstitutionPersons = computed(() => this.workplace().institutionPersons);

	/**
	 * Handles the navigation from the nav carousel.
	 * Updates the route based on the selected element.
	 */
	public onNavigate(event: { index: number; element: CarouselElement }) {
		this.router.navigate(['../', event.element.id], { relativeTo: this.route });
	}

	/**
	 * Navigates to 'edit' view.
	 */
	public openEdit() {
		if (!this.canEditInstitutions()) return;
		this.router.navigate(['edit'], { relativeTo: this.route });
	}

	/**
	 * Open delete dialog, reroutes after deletion.
	 */
	public async openDialog() {
		if (!this.canDeleteInstitutions()) return;

		await lastValueFrom(
			this.dialog
				.open(InstitutionWorkplaceDeleteDialog, {
					width: '400px',
					data: {
						workplace: this.workplace(),
						institutionId: this.institution().id,
						onDeleted: async () => {
							await this.router.navigate(['../'], { relativeTo: this.route });
						},
					},
				})
				.afterClosed(),
		);
	}
}
